/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { ErrorBox } from 'components/ErrorBox';
import {
  Checkbox,
  hasComplexity,
  hasMaxLength,
  hasMinLength,
  Input,
  isEmail,
  isRequired,
  PasswordStrength,
  Submit,
} from 'components/Form';
import { Link } from 'components/Link';
import { Disclaimer } from 'components/Page/Disclaimer';
import { RedirectContext } from 'context/Redirect';
import { StylingContextState } from 'context/Styling';
import { TrackingContext } from 'context/Tracking';
import { User, UserContext } from 'context/User';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { signInRoutes } from 'pages/SignIn/routes';
import { Endpoints, post } from 'utils/api';
import { TURNSTILE_ACTIVE, TURNSTILE_SITE_KEY } from 'utils/consts';
import { useHistory } from 'utils/router';
import { useStyles } from 'utils/useStyles';
import { getLegalTermsVersion } from 'utils/legalTerms';

import { isEmailTakenError } from './emailTakenError';
import { withoutLoginSource } from './loginSource';
import { signUpRoutes } from './routes';
import { isTurnstileTokenError } from './turnstileError';

interface CreateUserFormValues {
  login: string;
  password: string;
  subscribe: boolean;
  cfTurnstileToken?: string;
}

export interface CreateUserFormProps {
  onSubmit: () => void;
  onError: () => void;
  incomingEmail?: string;
}

const formName = 'sign-up';

export const CreateUserForm = ({ incomingEmail, onSubmit, onError }: CreateUserFormProps) => {
  const styles = useStyles((theme: StylingContextState) => getStyles(theme));
  const [, { getUser, setUser }] = useContext(UserContext);
  const [, { identifyUser, trackEvent, getSourcesAsQueryParams, getSSItem, faro }] = useContext(TrackingContext);
  const [, { getSignupRedirectUrl }] = useContext(RedirectContext);

  const history = useHistory();
  const { search } = useLocation();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    register,
    setValue,
  } = useForm<CreateUserFormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const [serverError, setServerError] = useState<string>();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | undefined>(undefined);

  // Register once so RHF tracks the field; no input element is mounted — setValue()
  // in the Turnstile callbacks below keeps the value in sync. The token is optional:
  // a missing/blocked challenge (e.g. privacy browsers that block the Cloudflare
  // script) no longer blocks submit — the request is sent without a token and
  // grafana-com records the absence server-side.
  useEffect(() => {
    register('cfTurnstileToken');
  }, [register]);

  useEffect(() => {
    if (!TURNSTILE_ACTIVE) return;
    const container = turnstileRef.current;
    if (!container) return;

    // The Cloudflare script in index.html uses `?render=explicit` and loads
    // `async defer`, so window.turnstile may not exist on first mount. Poll
    // briefly until it shows up, then render once.
    let widgetId: string | undefined;
    let intervalId: number | undefined;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile) return false;
      widgetId = window.turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        size: 'flexible',
        theme: 'light',
        // Fires when the user completes the challenge (or immediately for
        // managed/invisible). Also fires again on token refresh before expiry.
        callback: (token) => setValue('cfTurnstileToken', token, { shouldValidate: false }),
        // Clear the token on widget error/expiry so we never submit a stale value;
        // the widget re-fires its callback with a fresh token if the user completes it.
        'error-callback': () => setValue('cfTurnstileToken', '', { shouldValidate: false }),
        'expired-callback': () => setValue('cfTurnstileToken', '', { shouldValidate: false }),
      });
      // Store the id so we can reset the widget after a failed submit (tokens
      // are single-use); the cleanup below still uses the local `widgetId`.
      turnstileWidgetId.current = widgetId;
      return true;
    };

    if (!render()) {
      intervalId = window.setInterval(() => {
        if (render()) window.clearInterval(intervalId);
      }, 50);
    }

    return () => {
      cancelled = true;
      if (intervalId !== undefined) window.clearInterval(intervalId);
      if (widgetId !== undefined) window.turnstile?.remove(widgetId);
    };
  }, [setValue]);

  useEffectOnce(() => {
    if (incomingEmail) {
      setValue('login', incomingEmail, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    }
  });

  const sourceQueryParams = getSourcesAsQueryParams();
  const trackingQueryParams = sourceQueryParams ? `?${sourceQueryParams}` : '';

  const handleFormSubmit: SubmitHandler<CreateUserFormValues> = async (values) => {
    setServerError(undefined);

    onSubmit();

    try {
      faro.trackFormSubmit(formName);
      await post<User>(Endpoints.SIGN_UP, {
        email: values.login,
        password: values.password,
        signupForm: getSSItem('signupForm') || 'cloud_trial',
        subscribe: values.subscribe,
        msaSigned: true,
        redirectUrl: getSignupRedirectUrl(),
        // `|| undefined` so a blocked script (key omitted) and a cleared token from an
        // error/expiry (empty string) look identical on the wire — both send no token.
        cfTurnstileToken: TURNSTILE_ACTIVE ? values.cfTurnstileToken || undefined : undefined,
      });

      // Fire-and-forget: do not block redirect on legal fetch/accept. Signup already succeeded; if this
      // fails or finishes late, they can be prompted on next sign-in (nothing stored yet).
      void (async () => {
        try {
          const legalTermsVersion = await getLegalTermsVersion();
          if (legalTermsVersion) {
            await post(Endpoints.ACCEPT_LEGAL_TERMS, { legalTermsVersion });
          }
        } catch (err) {
          faro.trackError(err);
        }
      })();

      const newUserData = await getUser();
      if (newUserData?.id) {
        await identifyUser({
          id: newUserData.id,
          email: values.login,
        });
      }

      await setUser(newUserData);

      if (incomingEmail) {
        trackEvent({
          name: 'marketing_user_sign_up',
          email: values.login,
        });
      }

      faro.trackFormSubmitSuccess(formName);

      trackEvent({
        name: `create-account`,
      });

      history.push({
        pathname: `${signUpRoutes.checkInbox}`,
      });
    } catch (err) {
      faro.trackFormSubmitError(formName, err.message);
      faro.trackError(err);
      onError();
      setServerError(err.message);

      // Turnstile tokens are single-use; the server consumed it on this attempt.
      // Reset the widget so a fresh token is issued for the next submit.
      if (TURNSTILE_ACTIVE && turnstileWidgetId.current !== undefined) {
        // Clear the stale token; the widget re-fires its callback with a fresh one.
        setValue('cfTurnstileToken', '', { shouldValidate: false });
        window.turnstile?.reset(turnstileWidgetId.current);
      }
    }
  };

  return (
    <form id="form" noValidate onSubmit={handleSubmit(handleFormSubmit)}>
      <Input
        control={control}
        disabled={isSubmitting}
        errors={errors}
        id="email"
        label="Email"
        name="login"
        type="email"
        css={styles.inputs}
        validate={[isRequired('Email address is required'), isEmail('This email address is invalid')]}
      />
      <Input
        control={control}
        disabled={isSubmitting}
        errors={errors}
        id="password"
        label="Password"
        name="password"
        type="password"
        css={styles.inputs}
        validate={[
          isRequired('Password is required'),
          hasMinLength(8, 'Password must be at least %value% characters'),
          hasComplexity('Password must contain at least 1 character and 1 number'),
          hasMaxLength(150, 'Password must be at most %value% characters'),
        ]}
      />
      <PasswordStrength control={control} name="password" />
      <Checkbox
        control={control}
        disabled={isSubmitting}
        id="marketing-subscribe"
        label="Send me news and feature updates"
        name="subscribe"
        css={styles.inputs}
      />
      <Disclaimer trackingQueryParams={trackingQueryParams} align="center" />

      {/* Rendered only when REACT_APP_TURNSTILE_SITE_KEY is set and REACT_APP_ENABLE_TURNSTILE is true.*/}
      {TURNSTILE_ACTIVE && <div ref={turnstileRef} css={styles.turnstile} />}
      <Submit
        disabled={isSubmitting}
        id="submit"
        isLoading={isSubmitting}
        label="Create my account"
        loadingText="Creating account..."
        css={styles.button}
      />
      {serverError &&
        (isTurnstileTokenError(serverError) ? (
          <ErrorBox id="server-error" title="We couldn’t complete the security check" messageComponent="div">
            <p>
              Your browser’s privacy settings or a blocking extension may have prevented the Cloudflare security check
              from loading.
            </p>

            <p>
              <strong>To continue:</strong>
            </p>

            <ol>
              <li>Temporarily disable privacy protection, ad blocking, or script blocking for this site.</li>
              <li>
                If your browser or extension supports allowlists, allow <code>challenges.cloudflare.com</code>.
              </li>
              <li>Reload the page and try signing up again.</li>
            </ol>

            <p>If the problem continues, try using a different up-to-date browser.</p>
          </ErrorBox>
        ) : isEmailTakenError(serverError) ? (
          <ErrorBox id="server-error" title="This email already has an account">
            To continue, log in with this email.{' '}
            <Link
              id="email-taken-sign-in-link"
              label="Log in"
              to={`${signInRoutes.base}${withoutLoginSource(search)}`}
            />
          </ErrorBox>
        ) : (
          <ErrorBox id="server-error">{serverError}</ErrorBox>
        ))}
    </form>
  );
};

const getStyles = ({ colors }: StylingContextState) => ({
  inputs: css`
    margin: 1.1rem 0 16px;
    input {
      color: ${colors.gray11};

      &:focus,
      &:hover {
        background-color: ${colors.white};
      }
    }
  `,
  button: css`
    margin-top: 32px;
  `,
  turnstile: css`
    margin-top: 8px;
    width: 100%;

    /* Cloudflare injects a child container */
    > div {
      width: 100% !important;
    }

    iframe {
      width: 100% !important;
    }
  `,
});
